import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { SiteProposal } from '../DocumentAnalysisAPI';

/**
 * Die Frage, ob ein genanntes Gebäude zum Projekt gehört.
 *
 * Sie entsteht, wenn eine Auswertung fertig ist und ein Gebäude gebunden hat, dessen Anschrift
 * unter den Liegenschaften des Projekts fehlt. Der Gedanke dahinter: Ein Datenraum soll sein
 * Projekt aufbauen können — wer Verträge hineinzieht, hat schon gesagt, um welche Häuser es geht.
 *
 * **Gefragt wird, nicht getan.** Ein Dokument nennt auch Nachbaradressen, Vergleichsobjekte, den
 * Sitz einer Verwaltung. Nichts davon gehört ins Projekt, und ein Bestand, der von selbst wächst,
 * ist schwerer zu berichtigen als einer, der fragt.
 *
 * Der Beleg steht dabei: das Dokument, das die Anschrift genannt hat, und der Begriff, unter dem es
 * sie führt. Ohne ihn wäre es eine Adresse aus dem Nichts, zu der man nur raten kann.
 */

interface Properties {
    /** Woher die Frage kommt — die Auswertung und der Name ihres Dokuments. */
    analysisId: string;
    documentName: string;
    proposals: SiteProposal[];
    /** Übernehmen. Der Aufrufer weiß, wie es weitergeht, wenn mehrere anstehen. */
    onAccept: (proposal: SiteProposal) => Promise<void>;
    /** Diese eine Anschrift übergehen — die nächste kommt, sofern es eine gibt. */
    onSkip: () => void;
    /**
     * Die ganze Frage weglegen.
     *
     * Getrennt von `onSkip`, weil Escape und der Klick daneben etwas anderes meinen als der Knopf:
     * Wer wegklickt, will den Dialog los und nicht durch jede Anschrift einzeln. Auf denselben
     * Rückruf gelegt käme man aus einer Frage mit fünf Anschriften nur durch fünfmaliges Wegklicken
     * heraus.
     */
    onDismiss: () => void;
}

export default function SiteProposalDialog({
    analysisId,
    documentName,
    proposals,
    onAccept,
    onSkip,
    onDismiss,
}: Properties) {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);

    const proposal = proposals[0];
    if (!proposal) return <></>;

    const accept = async () => {
        setBusy(true);
        try {
            await onAccept(proposal);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog
            open
            onOpenChange={(next: boolean) => {
                // Nur schließen, nie öffnen: Der Aufrufer entscheidet, wann gefragt wird. Und
                // Wegklicken legt die ganze Frage weg, nicht nur diese eine Anschrift.
                if (!next && !busy) onDismiss();
            }}
        >
            <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-[520px]">
                <DialogHeader>
                    <DialogTitle>{t('portfolioDashboard.siteProposal.title')}</DialogTitle>
                    <DialogDescription>
                        {t('portfolioDashboard.siteProposal.explanation', { document: documentName })}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex min-w-0 items-start gap-2 rounded-md bg-muted/50 p-3">
                    <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    <div className="flex min-w-0 flex-col gap-0.5">
                        <span className="break-words text-sm font-medium">{proposal.label}</span>
                        {/* Der Begriff aus dem Dokument, wo er etwas hinzufügt — sonst stünde die
                            Anschrift zweimal da. */}
                        {proposal.term && proposal.term !== proposal.label ? (
                            <span className="break-words text-xs text-muted-foreground">
                                {t('portfolioDashboard.dataRoom.asWrittenIn', { term: proposal.term })}
                            </span>
                        ) : (
                            <></>
                        )}
                    </div>
                </div>

                {/* Wo mehrere anstehen, wird eines nach dem anderen gefragt: Jede Anschrift ist eine
                    eigene Entscheidung, und eine Sammelzusage verbirgt, wozu man ja sagt. */}
                {proposals.length > 1 ? (
                    <p className="text-xs text-muted-foreground">
                        {t('portfolioDashboard.siteProposal.more', { count: proposals.length - 1 })}
                    </p>
                ) : (
                    <></>
                )}

                <DialogFooter>
                    <Button type="button" variant="outline" onClick={onSkip} disabled={busy}>
                        {t('portfolioDashboard.siteProposal.skip')}
                    </Button>
                    <Button type="button" onClick={() => void accept()} disabled={busy} data-analysis={analysisId}>
                        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}
                        {t('portfolioDashboard.siteProposal.accept')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
